import axios from "axios";
import type {
  AccountLead,
  ArchiveUserResponse,
  ChangeEmailResponse,
  ClerkAccountStatus,
  ImpersonationStartResponse,
  UserListParams,
  UserListResponse,
  UserMutationResponse,
} from "@/interfaces/user";
import { apiClient } from "@/lib/apiClient";
import type { Role } from "@/lib/roles";
import { userListQuery } from "@/utils/usersOrgs-helper";

function parseMutationResponse(data: UserMutationResponse): UserMutationResponse {
  if (!data?.user?.id) {
    throw new Error("Server did not return synchronized user data.");
  }
  return data;
}

/** Users eligible to be an organization's account lead (feeds the Org Details picker). */
export async function fetchAccountLeads(): Promise<AccountLead[]> {
  const { data } = await apiClient.get<{ success: boolean; data: AccountLead[] }>(
    "/admin/users/account-leads",
  );
  return data.data ?? [];
}

export async function fetchUsers(
  params?: UserListParams & {
    // Kept alongside the panel filters: OrgUsersPanel/GroupUsersPanel/ApproversPanel pin the
    // list to one org and are not part of the new filter panel (D7 doesn't touch this param).
    organizationId?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page?: number;
    limit?: number;
  },
): Promise<UserListResponse> {
  const { organizationId, sortBy, sortDir, page, limit, ...filterParams } = params ?? {};
  const query: Record<string, string | number> = userListQuery(filterParams);
  if (organizationId) query.organizationId = organizationId;
  if (sortBy) query.sortBy = sortBy;
  if (sortDir) query.sortDir = sortDir;
  if (page && page > 1) query.page = page;
  if (limit) query.limit = limit;

  const { data } = await apiClient.get<UserListResponse>("/admin/users", { params: query });
  return data;
}

export async function updateUserRole(
  userId: number,
  role: Role | null,
  level?: "org" | "all",
): Promise<UserMutationResponse> {
  try {
    const { data } = await apiClient.patch<UserMutationResponse>(`/admin/users/${userId}/role`, {
      role,
      level,
    });
    return parseMutationResponse(data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error ? error : new Error("Failed to update role. Please try again.");
  }
}

export async function updateUserGroup(
  userId: number,
  groupId: number,
): Promise<UserMutationResponse> {
  try {
    const { data } = await apiClient.patch<UserMutationResponse>(`/admin/users/${userId}/group`, {
      groupId,
    });
    return parseMutationResponse(data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error ? error : new Error("Failed to update group. Please try again.");
  }
}

export async function updateUserAccountLead(
  userId: number,
  isAccountLead: boolean,
): Promise<UserMutationResponse> {
  try {
    const { data } = await apiClient.patch<UserMutationResponse>(
      `/admin/users/${userId}/account-lead`,
      { isAccountLead },
    );
    return parseMutationResponse(data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error
      ? error
      : new Error("Failed to update account lead. Please try again.");
  }
}

export async function updateUserName(
  userId: number,
  firstName: string,
  lastName: string,
): Promise<UserMutationResponse> {
  try {
    const { data } = await apiClient.patch<UserMutationResponse>(`/admin/users/${userId}/name`, {
      firstName,
      lastName,
    });
    return parseMutationResponse(data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error ? error : new Error("Failed to update name. Please try again.");
  }
}

export async function createUser(input: {
  email: string;
  role: Role;
  firstName: string;
  lastName?: string;
  groupId?: number;
  organizationId?: number;
}): Promise<UserMutationResponse> {
  try {
    const { data } = await apiClient.post<UserMutationResponse>("/admin/users", input);
    return parseMutationResponse(data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error ? error : new Error("Failed to create user. Please try again.");
  }
}

export async function archiveUser(userId: number): Promise<ArchiveUserResponse> {
  try {
    const { data } = await apiClient.delete<ArchiveUserResponse>(`/admin/users/${userId}`);
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Failed to archive user. Please try again.");
  }
}

export async function resendInvitation(userId: number): Promise<{ success: boolean }> {
  try {
    const { data } = await apiClient.post<{ success: boolean }>(
      `/admin/users/${userId}/resend-invitation`,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Failed to resend invitation. Please try again.");
  }
}

export async function fetchUserClerkAccount(userId: number): Promise<ClerkAccountStatus> {
  try {
    const { data } = await apiClient.get<{ success: boolean; account: ClerkAccountStatus }>(
      `/admin/users/${userId}/clerk-account`,
    );
    return data.account;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Couldn't load account details");
  }
}

export async function markUserEmailVerified(
  userId: number,
): Promise<{ alreadyVerified: boolean; account: ClerkAccountStatus }> {
  try {
    const { data } = await apiClient.post<{
      success: boolean;
      alreadyVerified: boolean;
      account: ClerkAccountStatus;
    }>(`/admin/users/${userId}/verify-email`);
    return { alreadyVerified: data.alreadyVerified, account: data.account };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Failed to mark email as verified. Please try again.");
  }
}

export async function changeUserEmail(userId: number, email: string): Promise<ChangeEmailResponse> {
  try {
    const { data } = await apiClient.patch<ChangeEmailResponse>(`/admin/users/${userId}/email`, {
      email,
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Failed to change email. Please try again.");
  }
}

export async function startImpersonation(
  targetUserId: string,
  actorId?: string,
): Promise<ImpersonationStartResponse> {
  try {
    const { data } = await apiClient.post<ImpersonationStartResponse>(
      "/admin/users/impersonation/start",
      {
        targetUserId,
        actorId,
      },
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Failed to start impersonation. Please try again.");
  }
}

/**
 * Completes self-service onboarding: creates the user's organization in the local DB,
 * links the user to it, and grants CLIENT_ADMIN. Returns `alreadyOnboarded: true` for
 * invited users who already have an org.
 */
export async function onboardUser(data: {
  firstName: string;
  lastName: string;
  organizationName: string;
  /** The Platform Terms version the signup screen displayed and the client accepted. */
  termsVersion: string;
  /** The checkbox wording as displayed, stored verbatim with the acceptance. */
  termsLabel?: string;
}): Promise<{ success: boolean; alreadyOnboarded?: boolean }> {
  try {
    const { data: response } = await apiClient.post<{
      success: boolean;
      alreadyOnboarded?: boolean;
    }>("/admin/users/onboard", data);
    return response;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error
      ? error
      : new Error("Failed to complete onboarding. Please try again.");
  }
}
